import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'

import { ArcadeIcon, InformationOutlineIcon } from '@arcade/web/icons/react'
import { ArcadeBorderedCard, Divider, Flex, PushCell, spacing, Stack, Text, Timeline } from '@cash-design-system/react'
import { FormBlockerElementPaymentPlanScheduleElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { getGlobalFontSize } from '@cash-web/shared/util-font-size'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { NestedCard } from '@cash-web/shared-feature-common'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

type PaymentPlanScheduleElementProps = FormBlockerElementPaymentPlanScheduleElement

const remToPx = (rem: number) => rem * getGlobalFontSize()

const Card = styled(NestedCard)`
  padding: ${({ theme }) => theme.spacing.l};
  border-radius: 1rem;
  width: 100%;
`

const FullWidthDivider = styled(Divider)`
  border-top: ${props => props.theme.pxToRem(2)} solid ${props => props.theme.colors.nestedCardOutline};
  margin: ${({ theme: { spacing } }) => `${spacing.l} -${spacing.l} ${spacing.l} -${spacing.l}`};
`

const RADIUS = remToPx(1.25)
const CIRCUMFERENCE = remToPx(2 * Math.PI * RADIUS)

const Visualization = styled.svg`
  width: 3rem;
  height: 3rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`

const Centered = styled.g`
  transform: translate(0.25rem, 0.25rem);
`

const Progress = styled.circle`
  fill: none;
  stroke: ${props => props.theme.arcadeColors.semantic.iconBrand};
  stroke-width: 0.5rem;
  stroke-linecap: round;
  stroke-dasharray: ${CIRCUMFERENCE};
`

const Label = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const PrimaryText = styled(Text)`
  text-align: center;
  margin-bottom: 0.125rem;
`
const SecondaryText = styled(Text)`
  text-align: center;
`

const IconContainer = styled.div`
  height: 1.5rem;
  display: grid;
  cursor: pointer;
`

const InfoIcon = styled(InformationOutlineIcon)`
  margin: auto;
`

const SourceIcon = styled.img`
  height: 1.5rem;
  width: 1.5rem;
`

const calculateOffset = (progress: number) => CIRCUMFERENCE * (1 - progress)

export function PaymentPlanScheduleElement({
  header,
  body,
  circle_progress_items,
  source_icon,
  source_text,
  source_info_click_action,
}: PaymentPlanScheduleElementProps) {
  const { t } = useTranslation()
  const textTokenizer = useTextTokenizer()
  const flow = useFlowContext()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  if (!header) return null

  if (isArcadeMigrationEnabled)
    return (
      <ArcadeBorderedCard data-testid="arcade-bordered-card">
        <Stack space={spacing.m}>
          <Text color="textStandard" typography="sectionTitle">
            {header}
          </Text>
          {body && (
            <Text color="textSubtle" typography="bodySmall">
              {body}
            </Text>
          )}
          {circle_progress_items && circle_progress_items.length > 0 && (
            <Timeline
              data-testid="arcade-timeline"
              events={
                circle_progress_items?.map(item => {
                  return {
                    status: 'PENDING',
                    value: item.primary_text,
                    title: item.secondary_text,
                  }
                }) ?? []
              }
            />
          )}
          {source_text && (
            <PushCell
              label={source_text}
              onClick={source_info_click_action ? () => flow.executeBlockerAction(source_info_click_action) : undefined}
              cellProps={{
                asset: {
                  Icon: InformationOutlineIcon as ArcadeIcon,
                },
              }}
            />
          )}
        </Stack>
      </ArcadeBorderedCard>
    )
  return (
    <Card>
      <Stack space={spacing.xxl}>
        <Stack space={spacing.l}>
          <Text as="p" typography="titleBarPageTitle">
            {textTokenizer(header)}
          </Text>
          {body && (
            <Text as="p" typography="body">
              {textTokenizer(body)}
            </Text>
          )}
        </Stack>
        <Flex flexDirection="row" flexWrap="wrap" gap={spacing.headerSpacing}>
          {circle_progress_items?.map(({ progress_percent = 0, circle_label, primary_text, secondary_text }) => {
            // Adjust progress_percent to prevent rendering a completed circle when progress is less than 100%
            const adjustedProgress = progress_percent < 1 ? Math.min(progress_percent, 0.9) : progress_percent

            const key = uuidv4()
            return (
              <div key={key}>
                <Visualization role="figure">
                  <Centered>
                    <Progress
                      cx={RADIUS}
                      cy={RADIUS}
                      r={RADIUS}
                      transform={`rotate(-90 ${RADIUS} ${RADIUS})`}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={progress_percent * 100}
                      aria-labelledby={`label-${key}`}
                      aria-describedby={`primary-text-${key} secondary-text-${key}`}
                    >
                      <animate
                        attributeName="stroke-dashoffset"
                        begin="0s"
                        dur="0.5s"
                        from={CIRCUMFERENCE}
                        to={calculateOffset(adjustedProgress)}
                        fill="freeze"
                      />
                    </Progress>
                    <foreignObject width={remToPx(2.5)} height={remToPx(2.5)} x={0} y={0}>
                      <Label id={`label-${key}`} typography="metaText">
                        {circle_label}
                      </Label>
                    </foreignObject>
                  </Centered>
                </Visualization>
                <PrimaryText as="p" typography="bodySmall" id={`primary-text-${key}`}>
                  {primary_text}
                </PrimaryText>
                <SecondaryText as="p" typography="helpText" id={`secondary-text-${key}`}>
                  {secondary_text}
                </SecondaryText>
              </div>
            )
          })}
        </Flex>
      </Stack>
      {source_text && <FullWidthDivider />}
      {source_text && (
        <Flex gap={spacing.s}>
          {source_icon && <SourceIcon src={source_icon.light_url} alt="" aria-hidden={true} />}
          <Text typography="labelMedium">{source_text}</Text>
          {source_info_click_action && (
            <IconContainer
              role="button"
              onClick={() => flow.executeBlockerAction(source_info_click_action)}
              tabIndex={0}
              aria-label={t('Payment plan source information', {
                context: 'Payment plan schedule source information button aria label',
              })}
            >
              <InfoIcon size={16} />
            </IconContainer>
          )}
        </Flex>
      )}
    </Card>
  )
}
